import axios from "axios"
import { useAuth0 } from "@auth0/auth0-react"
import { useQuery } from "@tanstack/react-query"
import { usePermissionsList } from "./queries/usePermissions"

/**
 * @param {Object} [options]
 * @param {boolean} [options.clinicalOnly] - When true, the server narrows
 *   `settingsData.nurses` to roster entries with an RN or LVN role. Used by
 *   the Future Huddle staffing dropdown to exclude MAs and Care Partners.
 *   Each variant has its own React Query cache key.
 */
const useFetchSettings = ({ clinicalOnly = false } = {}) => {
	const { getAccessTokenSilently, isAuthenticated } = useAuth0()
	const permissionsData = usePermissionsList()
	const canRead = permissionsData?.includes("read:settings") ?? false

	const query = useQuery({
		queryKey: ["settings", clinicalOnly ? "clinical" : "all"],
		queryFn: async () => {
			const token = await getAccessTokenSilently({
				authorizationParams: { audience: process.env.REACT_APP_API_IDENTIFIER }
			})
			const url = clinicalOnly
				? `${process.env.REACT_APP_BACKEND_URL}/api/settings?clinicalOnly=true`
				: `${process.env.REACT_APP_BACKEND_URL}/api/settings`
			const response = await axios.get(url, {
				headers: { Authorization: `Bearer ${token}` }
			})
			return response.data.payload
		},
		enabled: isAuthenticated && canRead,
		staleTime: 1000 * 60 * 10,
		refetchOnMount: "always"
	})

	return {
		settingsData: query.data,
		settingsStatus: query.isLoading
			? "loading"
			: query.isError
				? "failed"
				: query.data
					? "succeeded"
					: "idle",
		settingsError: query.error?.message ?? null
	}
}

export default useFetchSettings
